import React from 'react';
import { ChartEvent } from './chart/types';
import { CareflowAuditEntry } from './api/careflow360Api';
import { useI18n } from '../../i18n/I18nProvider';

interface CareFlow360TimelineProps {
  currentTreatmentEvents: ChartEvent[];
  currentHistoryEvents: ChartEvent[];
  historicalEvents: ChartEvent[];
  auditTrail: CareflowAuditEntry[];
  historyTooth: string;
  setHistoryTooth: (v: string) => void;
  historyLabel: string;
  setHistoryLabel: (v: string) => void;
  historyNote: string;
  setHistoryNote: (v: string) => void;
  historySaving: boolean;
  handleAddHistory: () => Promise<void>;
  handleDeleteHistory: (event: ChartEvent) => Promise<void>;
  formatDateTime: (iso?: string | null) => string;
}

export default function CareFlow360Timeline({
  currentTreatmentEvents,
  currentHistoryEvents,
  historicalEvents,
  auditTrail,
  historyTooth,
  setHistoryTooth,
  historyLabel,
  setHistoryLabel,
  historyNote,
  setHistoryNote,
  historySaving,
  handleAddHistory,
  handleDeleteHistory,
  formatDateTime,
}: CareFlow360TimelineProps) {
  const { t } = useI18n();

  return (
    <div className="cf360-timeline-view">
      <h3>📋 {t('careflow_clinical_timeline')}</h3>
      
      <div className="cf360-event-groups">
        <h4 className="events-group-title">⚡ {t('careflow_current_treatments')}</h4>
        <ul className="cf360-event-list">
          {currentTreatmentEvents.map((ev) => (
            <li key={`tx-${ev.id}`}>
              <strong>{t('careflow_tooth_prefix')} {ev.toothId}</strong>: {ev.label}
              <span className="ev-type">{ev.type}</span>
              {ev.note && <div className="ev-note">{ev.note}</div>}
            </li>
          ))}
          {currentTreatmentEvents.length === 0 && (
            <li className="cf360-muted-item">{t('careflow_no_treatment_events')}</li>
          )}
        </ul>

        <h4 className="events-group-title mt-4">📜 {t('careflow_current_history')}</h4>
        <div className="cf360-history-form">
          <input
            value={historyTooth}
            onChange={(e) => setHistoryTooth(e.target.value)}
            placeholder={t('careflow_history_tooth')}
          />
          <input
            value={historyLabel}
            onChange={(e) => setHistoryLabel(e.target.value)}
            placeholder={t('careflow_history_label')}
          />
          <input
            value={historyNote}
            onChange={(e) => setHistoryNote(e.target.value)}
            placeholder={t('careflow_history_note')}
          />
          <button
            type="button"
            className="cf360-small-btn"
            onClick={handleAddHistory}
            disabled={historySaving}
          >
            {historySaving ? t('careflow_add_note_saving') : t('careflow_add_history')}
          </button>
        </div>
        
        <ul className="cf360-event-list history-list">
          {currentHistoryEvents.map((ev) => (
            <li key={`hist-current-${ev.id}`} className="historical-item">
              <strong>{t('careflow_tooth_prefix')} {ev.toothId}</strong>: {ev.label}
              {ev.note && <div className="ev-note">{ev.note}</div>}
              <button
                type="button"
                className="cf360-link-btn"
                onClick={() => void handleDeleteHistory(ev)}
              >
                {t('careflow_remove')}
              </button>
            </li>
          ))}
          {currentHistoryEvents.length === 0 && (
            <li className="cf360-muted-item">{t('careflow_no_history_entries')}</li>
          )}
        </ul>

        {historicalEvents.length > 0 && (
          <>
            <h4 className="events-group-title mt-4">🏛️ {t('careflow_past_records')}</h4>
            <ul className="cf360-event-list history-list">
              {historicalEvents.map((ev) => (
                <li key={`hist-${ev.id}`} className="historical-item">
                  <strong>{t('careflow_tooth_prefix')} {ev.toothId}</strong>: {ev.label}
                  <span className="ev-type">{ev.domain || t('careflow_event_label')}</span>
                  <span className="ev-history-tag">{t('careflow_historical_label')}</span>
                  {ev.note && <div className="ev-note">{ev.note}</div>}
                </li>
              ))}
            </ul>
          </>
        )}

        {auditTrail.length > 0 && (
          <>
            <h4 className="events-group-title mt-4">🔍 {t('careflow_audit_trail')}</h4>
            <ul className="cf360-audit-list">
              {auditTrail.map((entry) => (
                <li
                  key={`audit-${entry.id}`}
                  className={`audit-item audit-${String(entry.action || '').toLowerCase()}`}
                >
                  <div className="audit-row-top">
                    <span className="audit-action">{(entry.action || 'updated').toUpperCase()}</span>
                    <span className="audit-time">{formatDateTime(entry.created_at)}</span>
                  </div>
                  <div className="audit-row-main">
                    <strong>{entry.tooth_number ? `${t('careflow_tooth_prefix')} ${entry.tooth_number}` : t('careflow_general_label')}</strong>
                    {entry.diagnosis ? `: ${entry.diagnosis}` : ''}
                  </div>
                  {entry.note && <div className="ev-note">{entry.note}</div>}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
